export const SELL_TYPES = [
  'Physical Business (Brick And Mortar)',
  'Online / E-commerce',
  'Both Physical and Online',
] as const;

export const ID_TYPES = [
  'Passport',
  'Driver License',
  'National ID Card',
  'Residence Permit',
] as const;

/** ID types that require a back-of-card image for Rapyd KYB. */
export const ID_TYPES_REQUIRING_BACK = new Set<string>([
  'Driver License',
  'National ID Card',
  'Residence Permit',
]);

export const OWNER_ROLES = [
  'CEO',
  'CTO',
  'CFO',
  'Treasurer',
  'President',
  'Vice President',
  'Secretary',
  'Chair of Board',
  'Director',
  'Other',
] as const;

export const DIAL_CODES = [
  '+44', '+1', '+91', '+971', '+966', '+33', '+49', '+34', '+39', '+31', '+46', '+351', '+48', '+353',
  '+61', '+65', '+81', '+82', '+86', '+852', '+32', '+41', '+43', '+45', '+47', '+358', '+420',
  '+36', '+30', '+40', '+380', '+90', '+27', '+234', '+254', '+20', '+92', '+880', '+94', '+66', '+62',
  '+63', '+84', '+64', '+52', '+55', '+54', '+56', '+57', '+51',
] as const;

/** Include a login/IP dial code even if it is not in the curated list, so the phone select never goes blank. */
export function normalizeDialCode(value?: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const withPlus = raw.startsWith('+') ? raw : `+${raw.replace(/^\+/, '')}`;
  return withPlus;
}

export function dialCodeOptions(current?: string): string[] {
  const extra = normalizeDialCode(current);
  const base = Array.from(new Set(DIAL_CODES.map(String)));
  if (extra && extra.startsWith('+') && !base.includes(extra)) return [extra, ...base];
  return base;
}

export function parseDialCode(phone: string): { dialCode: string; localNumber: string } {
  const trimmed = String(phone || '').trim();
  const codes = Array.from(new Set(DIAL_CODES.map(String))).sort((a, b) => b.length - a.length);
  for (const code of codes) {
    if (trimmed.startsWith(code)) {
      return { dialCode: code, localNumber: trimmed.slice(code.length).replace(/\D/g, '') };
    }
  }
  const match = trimmed.match(/^(\+\d{1,4})(\d+)$/);
  if (match) return { dialCode: match[1], localNumber: match[2] };
  return { dialCode: '+44', localNumber: trimmed.replace(/\D/g, '') };
}

export function isValidUsdAmount(value: string): boolean {
  if (!value.trim()) return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}

export function isValidPositiveInt(value: string): boolean {
  if (!value.trim()) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= 0;
}

export function isValidStatementDescriptor(value: string): boolean {
  const v = value.trim();
  return v.length >= 5 && v.length <= 22;
}

export function isAdultDob(value: string): boolean {
  if (!value) return false;
  const dob = new Date(value);
  if (Number.isNaN(dob.getTime())) return false;
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 18);
  return dob <= cutoff;
}

/** UK Companies House: 8 digits, or 2 letters + 6 digits (e.g. OC123456). */
export function isValidGbCompanyRegistration(value: string): boolean {
  const raw = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  return /^\d{8}$/.test(raw) || /^[A-Z]{2}\d{6}$/.test(raw);
}

/**
 * Soft registration check by country.
 * GB is strict; other countries require a non-empty alphanumeric value (no spaces-only / free prose).
 */
export function isValidCompanyRegistration(value: string, countryCode: string): boolean {
  const country = String(countryCode || '').toUpperCase().slice(0, 2);
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (country === 'GB' || country === 'UK') return isValidGbCompanyRegistration(raw);
  // Disallow obvious free-text company names (letters-only long strings without digits)
  const compact = raw.replace(/\s+/g, '');
  if (compact.length < 4) return false;
  if (/^[A-Za-z\s.'-]{10,}$/.test(raw) && !/\d/.test(raw)) return false;
  return /^[A-Za-z0-9][A-Za-z0-9\-/]*$/.test(compact);
}

export function normalizeCompanyRegistrationInput(value: string, countryCode: string): string {
  const country = String(countryCode || '').toUpperCase().slice(0, 2);
  if (country === 'GB' || country === 'UK') {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
  }
  return value.replace(/[^A-Za-z0-9\-/]/g, '').slice(0, 40);
}

export function isValidOwnershipPercent(value: string): boolean {
  if (!String(value || '').trim()) return false;
  const n = Number(String(value).replace(/%/g, '').trim());
  return Number.isFinite(n) && n >= 25 && n <= 100;
}

export function requiresIdBack(idType: string): boolean {
  return ID_TYPES_REQUIRING_BACK.has(String(idType || '').trim());
}

export function isValidWebsiteUrl(value: string): boolean {
  const v = String(value || '').trim();
  if (!v) return false;
  try {
    const withProtocol = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    const u = new URL(withProtocol);
    return Boolean(u.hostname && u.hostname.includes('.'));
  } catch {
    return false;
  }
}
